export type ExecutiveTone = 'brand' | 'sky' | 'emerald' | 'amber' | 'red' | 'violet'

export interface ExecutiveKpi {
  label: string
  value: string
  detail: string
  trend: string
  tone: ExecutiveTone
  destination: string
}

export interface CommandAlert {
  id: string
  severity: 'critical' | 'warning' | 'info'
  title: string
  detail: string
  source: string
  time: string
  destination: string
}

export interface BedCommandCell {
  name: string
  occupied: number
  total: number
  available: number
  status: 'available' | 'reserved' | 'cleaning' | 'occupied' | 'critical' | 'isolation'
  forecast: string
}

export const executiveKpis: ExecutiveKpi[] = [
  { label: 'Hospital census', value: '481', detail: 'Of 550 licensed beds', trend: '+14 vs yesterday', tone: 'brand', destination: '/beds' },
  { label: 'Available beds', value: '41', detail: '29 ready now · 12 expected', trend: 'Watch capacity', tone: 'emerald', destination: '/beds' },
  { label: 'Bed occupancy', value: '87.5%', detail: 'Target ≤ 85%', trend: '+2.3 pts', tone: 'amber', destination: '/beds' },
  { label: 'Today’s admissions', value: '68', detail: 'Forecast 94 by midnight', trend: '+11% vs avg', tone: 'sky', destination: '/admissions/new' },
  { label: 'Today’s discharges', value: '54', detail: '7 likely before noon', trend: '13 pending actions', tone: 'emerald', destination: '/beds' },
  { label: 'ER waiting', value: '17', detail: 'Door-to-doctor 14 min', trend: '+18% vs yesterday', tone: 'red', destination: '/emergency' },
  { label: 'ICU occupancy', value: '95%', detail: '19 of 20 staffed beds', trend: '98% in 6 hours', tone: 'red', destination: '/beds' },
  { label: 'OT utilization', value: '78%', detail: '6 of 8 rooms running', trend: '2 delayed first cases', tone: 'amber', destination: '/ot' },
  { label: 'OPD patients', value: '1,284', detail: '126 waiting across clinics', trend: '+6.4% today', tone: 'sky', destination: '/appointments' },
  { label: 'Lab samples', value: '892', detail: '3 critical value escalations', trend: 'TAT +14%', tone: 'violet', destination: '/lab' },
  { label: 'Radiology cases', value: '241', detail: '12 reporting breaches', trend: 'CT queue elevated', tone: 'violet', destination: '/radiology' },
  { label: 'Revenue today', value: '₨ 18.6M', detail: 'Cash collection ₨ 7.8M', trend: '+11% vs average', tone: 'emerald', destination: '/billing' },
]

export const commandAlerts: CommandAlert[] = [
  { id: 'alert-1', severity: 'critical', title: 'ICU staffed capacity at 95%', detail: 'One staffed bed remains; two emergency transfers are expected within 45 minutes.', source: 'Bed command', time: 'Now', destination: '/beds' },
  { id: 'alert-2', severity: 'critical', title: 'Critical laboratory result', detail: 'Potassium 6.8 mmol/L requires documented clinician read-back.', source: 'Laboratory', time: '2 min ago', destination: '/lab' },
  { id: 'alert-3', severity: 'warning', title: 'Emergency arrivals exceeding plan', detail: 'ER demand is 18% above yesterday; 6 patients are boarding for inpatient beds.', source: 'Emergency', time: '7 min ago', destination: '/emergency' },
  { id: 'alert-4', severity: 'warning', title: 'Noradrenaline safety stock', detail: 'ICU imprest has 6 ampoules remaining; emergency replenishment awaits approval.', source: 'Pharmacy', time: '18 min ago', destination: '/inventory' },
]

export const bedCommandCells: BedCommandCell[] = [
  { name: 'Medicine', occupied: 98, total: 112, available: 8, status: 'occupied', forecast: '6 discharges expected' },
  { name: 'Surgery', occupied: 74, total: 86, available: 9, status: 'occupied', forecast: '3 cleaning · 4 ready' },
  { name: 'Orthopedics', occupied: 42, total: 48, available: 4, status: 'reserved', forecast: '2 ER holds' },
  { name: 'ICU', occupied: 19, total: 20, available: 1, status: 'critical', forecast: '2 transfers pending' },
  { name: 'CCU', occupied: 14, total: 16, available: 2, status: 'critical', forecast: '1 STEMI inbound' },
  { name: 'NICU', occupied: 18, total: 20, available: 2, status: 'isolation', forecast: '4 isolation beds' },
  { name: 'Pediatrics', occupied: 47, total: 54, available: 5, status: 'available', forecast: '2 expected admissions' },
  { name: 'Oncology', occupied: 31, total: 36, available: 3, status: 'cleaning', forecast: '2 turnover in progress' },
  { name: 'Women’s Health', occupied: 58, total: 72, available: 10, status: 'available', forecast: '5 planned admissions' },
  { name: 'Private rooms', occupied: 52, total: 66, available: 8, status: 'reserved', forecast: '6 corporate holds' },
]

export const occupancyTrend = [
  { time: '00:00', actual: 82, forecast: 82, target: 85 },
  { time: '03:00', actual: 83, forecast: 83, target: 85 },
  { time: '06:00', actual: 85, forecast: 85, target: 85 },
  { time: '09:00', actual: 87, forecast: 87, target: 85 },
  { time: '12:00', actual: 88, forecast: 88, target: 85 },
  { time: '15:00', actual: 87.5, forecast: 89, target: 85 },
  { time: '18:00', actual: null, forecast: 91, target: 85 },
  { time: '21:00', actual: null, forecast: 92, target: 85 },
]

export const patientFlowTrend = [
  { hour: '08', arrivals: 34, discharges: 8 },
  { hour: '10', arrivals: 48, discharges: 11 },
  { hour: '12', arrivals: 56, discharges: 16 },
  { hour: '14', arrivals: 62, discharges: 12 },
  { hour: '16', arrivals: 71, discharges: 9 },
  { hour: '18', arrivals: 67, discharges: 14 },
]

export const operationalSignals = [
  { name: 'Emergency', value: '17 waiting', target: '≤ 12', note: '6 boarding · 3 ambulances inbound', tone: 'red' as const, destination: '/emergency' },
  { name: 'Operation theatres', value: '78% utilized', target: '≥ 82%', note: '2 first-case delays · 1 room available', tone: 'amber' as const, destination: '/ot' },
  { name: 'Diagnostics', value: '94.2% TAT', target: '≥ 95%', note: '3 critical lab · 12 imaging reports overdue', tone: 'violet' as const, destination: '/lab' },
  { name: 'Pharmacy', value: '27 queued', target: '≤ 20', note: '2 controlled-drug approvals · 1 stock-out risk', tone: 'amber' as const, destination: '/pharmacy' },
  { name: 'Workforce', value: '92% rostered', target: '≥ 95%', note: '3 departments need staffing support', tone: 'red' as const, destination: '/hr' },
  { name: 'Supply chain', value: '9 alerts', target: '0 critical', note: '₨ 1.8M near-expiry exposure', tone: 'amber' as const, destination: '/inventory' },
]

export const aiInsights = [
  { title: 'Protect ICU capacity', detail: 'ICU occupancy is projected to reach 98% within 6 hours. Expedite two transfer-ready patients and activate the step-down escalation plan.', tone: 'critical' as const, action: 'Open bed command', destination: '/beds' },
  { title: 'Recover emergency flow', detail: 'ER congestion is 18% above yesterday. Seven patients meet likely discharge criteria before noon; coordinate a 10:30 discharge huddle.', tone: 'warning' as const, action: 'Open emergency command', destination: '/emergency' },
  { title: 'Stabilize diagnostic turnaround', detail: 'Laboratory turnaround time has increased 14%. Route two float technologists to chemistry until the 16:00 peak clears.', tone: 'info' as const, action: 'Open laboratory', destination: '/lab' },
]
